import { useState } from 'react';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetDescription, 
  SheetTrigger 
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShoppingCart, Minus, Plus, Trash2 } from 'lucide-react';
import { PayPalButtons } from "@paypal/react-paypal-js";
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { CartStripePayment } from './CartStripePayment';
import { useMemo } from 'react';


// CartButton Component - This will be the main entry point to the cart
export const CartButton = () => {
  const { state } = useCart();
  const itemCount = state.items.length;
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Cart
          {itemCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
              {itemCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <CartDrawer />
    </Sheet>
  );
};

// CartDrawer Component - This handles displaying cart contents and checkout
const CartDrawer = () => {
  const { state, removeFromCart, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

const updatePhotographerBalance = async (
  photographerId: string,
  amount: number
) => {
  // Get current balance
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('balance')
    .eq('id', photographerId)
    .single();

  if (profileError) throw profileError;

  // Update balance
  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      balance: (profile.balance || 0) + amount
    })
    .eq('id', photographerId);

  if (updateError) throw updateError;
};

  const handlePurchase = async (data: any, actions: any) => {
    try {
      setLoading(true);
      const orderDetails = await actions.order.capture();
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error("Failed to get user information");

      // Process each item in the cart sequentially
      for (const item of state.items) {
        // First check if user already purchased this photo
        const { data: existingPurchase, error: checkError } = await supabase
          .from('purchased_photos')
          .select('*')
          .eq('user_id', user.id)
          .eq('photo_id', item.photo.id)
          .single();
  
        if (checkError && checkError.code !== 'PGRST116') { // PGRST116 means not found
          throw new Error('Failed to check existing purchase');
        }
  
        // Skip if already purchased
        if (existingPurchase) {
          continue;
        }
  
        // Calculate fees
        const platformFee = item.photo.price * item.quantity * 0.15;
        const photographerAmount = item.photo.price * item.quantity * 0.85;
  
        // First create the transaction record and get its ID
        const { data: transaction, error: transactionError } = await supabase
          .from('transactions')
          .insert({
            order_id: orderDetails.id,
            photo_id: item.photo.id,
            buyer_id: user.id,
            photographer_id: item.photo.user_id,
            amount: item.photo.price * item.quantity,
            platform_fee: platformFee,
            photographer_amount: photographerAmount,
            status: 'completed'
          })
          .select()  // This returns the created transaction
          .single();
  
        if (transactionError) {
          console.error('Transaction creation failed:', transactionError);
          throw new Error('Failed to record transaction');
        }
  
        // Then create the purchase record using the transaction's ID
        const { error: purchaseError } = await supabase
          .from('purchased_photos')
          .insert({
            user_id: user.id,
            photo_id: item.photo.id,
            created_at: new Date().toISOString()  // Explicitly set creation time
          });
  
        if (purchaseError) {
          // If purchase record fails, we should probably roll back the transaction
          console.error('Purchase record failed:', purchaseError);
          await supabase
            .from('transactions')
            .delete()
            .eq('id', transaction.id);
          throw new Error('Failed to record purchase');
        }

        await updatePhotographerBalance(
            item.photo.user_id,
            photographerAmount
        );
      }
      toast({
        title: "Success",
        description: "Your purchase was completed successfully!",
      });
      clearCart();
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to process payment",
      });
    } finally {
      setLoading(false);
    }
  };
  const cartPhotos = useMemo(() => 
    state.items.map(item => item.photo), 
    [state.items]
  );
  

  return (
        <SheetContent className="max-h-full w-[400px] overflow-y-auto">
        <SheetHeader>
        <SheetTitle>Shopping Cart</SheetTitle>
        <SheetDescription>
          View and manage items in your shopping cart
        </SheetDescription>
      </SheetHeader>

      {state.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
          <ShoppingCart className="h-12 w-12 mb-4" />
          <p>Your cart is empty</p>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <ScrollArea className="flex-grow">
            <div className="space-y-4 pt-4">
            {state.items.map((item) => (
          <div key={item.photo.id} className="flex items-center gap-4 p-4 border rounded-lg">
            <img
              src={item.photo.watermark_url}
              alt={item.photo.title || 'Photo'}
              className="w-20 h-20 object-cover rounded"
            />
            <div className="flex-grow">
              <h3 className="font-medium">{item.photo.title || 'Untitled Photo'}</h3>
              
              {/* Price display with discount if applicable */}
              {(item.discountedPrice !== undefined && item.appliedDiscount > 0) ? (
              <div>
                <p className="text-sm text-gray-500 line-through">
                  ${item.originalPrice?.toFixed(2) || item.photo.price?.toFixed(2)}
                </p>
                <p className="text-sm text-green-600">
                  ${item.discountedPrice?.toFixed(2)} 
                  <span className="ml-1 text-xs">
                    ({item.appliedDiscount}% off)
                  </span>
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">${item.photo.price?.toFixed(2)}</p>
            )}
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => removeFromCart(item.photo.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
            </div>
          </ScrollArea>

          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between mb-4">
              <span className="font-medium">Total:</span>
              <span className="font-bold">${state.total.toFixed(2)}</span>
            </div>
              {/* Stripe payment option */}
              <CartStripePayment
                photos={cartPhotos}
                onSuccess={(paymentIntentId) => {
                  // Removed clearCart() from here since it's now handled in the form
                  toast({
                    title: "Success",
                    description: "Your photos have been purchased successfully!",
                  });
                }}
                onError={(error) => {
                  console.error("Stripe error:", error);
                  toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Payment failed. Please try again.",
                  });
                }}
                disabled={loading}
              />

            <PayPalButtons
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: state.total.toFixed(2)
                    }
                  }]
                });
              }}
              onApprove={handlePurchase}
              disabled={loading}
              style={{ height: 35 }}
            />

            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={clearCart}
              disabled={loading}
            >
              Clear Cart
            </Button>
          </div>
        </div>
      )}
    </SheetContent>
  );
};